import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Sparkles, Target, CheckCircle2, Circle, BookOpen, ClipboardList, Bell, Loader2, Brain, TrendingUp, Calendar } from "lucide-react";
import { generateRevisionPlan, getLatestRevisionPlan, toggleReminderDone } from "@/lib/revision-coach.functions";
import { useServerFn } from "@tanstack/react-start";

export const Route = createFileRoute("/learner/coach")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "AI Revision Coach · MAKAI" }] }),
  component: CoachPage,
});

interface PlanShape {
  summary?: string;
  readiness_score?: number;
  readiness_label?: string;
  strengths?: string[];
  weaknesses?: { subject: string; topic?: string; note: string }[];
  daily_schedule?: { day: string; blocks: { time: string; subject: string; activity: string; minutes: number }[] }[];
  recommended_lessons?: { id?: string; title: string; subject?: string; reason: string }[];
  recommended_quizzes?: { id?: string; title: string; subject?: string; reason: string }[];
  motivational_note?: string;
}

interface PlanRow { id: string; plan: PlanShape; readiness_score: number | null; generated_at: string; term_label: string | null }
interface Reminder { id: string; title: string; subject: string | null; scheduled_for: string; done: boolean }

function CoachPage() {
  const fetchLatest = useServerFn(getLatestRevisionPlan);
  const generate = useServerFn(generateRevisionPlan);
  const toggle = useServerFn(toggleReminderDone);

  const [plan, setPlan] = useState<PlanRow | null>(null);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchLatest({});
      setPlan(r.plan as PlanRow | null);
      setReminders((r.reminders ?? []) as Reminder[]);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [fetchLatest]);

  useEffect(() => { load(); }, [load]);

  const onGenerate = async () => {
    setGenerating(true); setError(null);
    try { await generate({}); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setGenerating(false); }
  };

  const onToggle = async (id: string, done: boolean) => {
    setReminders((rs) => rs.map((r) => r.id === id ? { ...r, done } : r));
    try { await toggle({ data: { id, done } }); } catch { /* revert silent */ }
  };

  const p = plan?.plan;
  const readiness = plan?.readiness_score ?? p?.readiness_score ?? null;

  return (
    <AppShell role="learner" title="AI Revision Coach" subtitle="Your personalised study plan, built from your real progress."
      action={
        <Button onClick={onGenerate} disabled={generating} className="gap-2">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {plan ? "Refresh plan" : "Generate plan"}
        </Button>
      }>
      {error && <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

      {loading ? (
        <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !plan ? (
        <EmptyState onGenerate={onGenerate} generating={generating} />
      ) : (
        <div className="space-y-6">
          {/* Readiness hero */}
          <section className="rounded-3xl border border-border bg-gradient-to-br from-primary-soft via-card to-card p-6 shadow-soft">
            <div className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center">
              <ReadinessGauge score={readiness ?? 0} label={p?.readiness_label ?? "Building"} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
                  <Brain className="h-3.5 w-3.5" /> Predicted exam readiness
                </div>
                <h2 className="mt-1 font-display text-xl font-bold">{p?.readiness_label ?? "Building foundation"}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{p?.summary}</p>
                {p?.motivational_note && (
                  <p className="mt-3 rounded-xl bg-gold/10 px-3 py-2 text-sm italic text-foreground">"{p.motivational_note}"</p>
                )}
                <p className="mt-3 text-[11px] text-muted-foreground">Generated {new Date(plan.generated_at).toLocaleString()} · {plan.term_label}</p>
              </div>
            </div>
          </section>

          {/* Strengths & weaknesses */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel icon={TrendingUp} title="Strengths">
              {(p?.strengths ?? []).length ? (
                <ul className="space-y-2">
                  {p!.strengths!.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm"><CheckCircle2 className="h-4 w-4 shrink-0 text-primary" /> {s}</li>
                  ))}
                </ul>
              ) : <Empty>Not enough data yet.</Empty>}
            </Panel>
            <Panel icon={Target} title="Focus areas">
              {(p?.weaknesses ?? []).length ? (
                <ul className="space-y-3">
                  {p!.weaknesses!.map((w, i) => (
                    <li key={i} className="rounded-xl border border-border/60 bg-muted/40 p-3">
                      <div className="text-sm font-semibold">{w.subject}{w.topic ? ` · ${w.topic}` : ""}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{w.note}</div>
                    </li>
                  ))}
                </ul>
              ) : <Empty>No weaknesses identified — keep going!</Empty>}
            </Panel>
          </div>

          {/* Daily schedule */}
          <Panel icon={Calendar} title="7-day revision schedule">
            {(p?.daily_schedule ?? []).length ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
                {p!.daily_schedule!.map((d, i) => (
                  <div key={i} className="rounded-2xl border border-border/60 bg-card p-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-primary">{d.day}</div>
                    <div className="mt-2 space-y-2">
                      {(d.blocks ?? []).map((b, j) => (
                        <div key={j} className="rounded-lg bg-muted/40 p-2">
                          <div className="flex items-center justify-between text-[11px] font-semibold text-muted-foreground">
                            <span>{b.time}</span><span>{b.minutes}m</span>
                          </div>
                          <div className="mt-0.5 text-sm font-semibold">{b.subject}</div>
                          <div className="text-xs text-muted-foreground">{b.activity}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : <Empty>No schedule yet.</Empty>}
          </Panel>

          {/* Recommendations */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel icon={BookOpen} title="Recommended lessons">
              {(p?.recommended_lessons ?? []).length ? (
                <ul className="space-y-2">
                  {p!.recommended_lessons!.map((l, i) => (
                    <li key={i} className="rounded-xl border border-border/60 p-3">
                      <div className="text-sm font-semibold">{l.title}</div>
                      {l.subject && <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l.subject}</div>}
                      <div className="mt-1 text-xs text-muted-foreground">{l.reason}</div>
                    </li>
                  ))}
                </ul>
              ) : <Empty>No recommendations.</Empty>}
            </Panel>
            <Panel icon={ClipboardList} title="Practice quizzes">
              {(p?.recommended_quizzes ?? []).length ? (
                <ul className="space-y-2">
                  {p!.recommended_quizzes!.map((q, i) => (
                    <li key={i} className="rounded-xl border border-border/60 p-3">
                      <div className="text-sm font-semibold">{q.title}</div>
                      {q.subject && <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{q.subject}</div>}
                      <div className="mt-1 text-xs text-muted-foreground">{q.reason}</div>
                    </li>
                  ))}
                </ul>
              ) : <Empty>No quizzes recommended.</Empty>}
            </Panel>
          </div>

          {/* Reminders */}
          <Panel icon={Bell} title="Upcoming revision reminders">
            {reminders.length ? (
              <ul className="space-y-2">
                {reminders.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
                    <button onClick={() => onToggle(r.id, !r.done)} aria-label="toggle">
                      {r.done ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-sm font-semibold ${r.done ? "line-through text-muted-foreground" : ""}`}>{r.title}</div>
                      <div className="text-[11px] text-muted-foreground">{new Date(r.scheduled_for).toLocaleString()}{r.subject ? ` · ${r.subject}` : ""}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <Empty>No reminders yet.</Empty>}
          </Panel>
        </div>
      )}
    </AppShell>
  );
}

function ReadinessGauge({ score, label }: { score: number; label: string }) {
  const pct = Math.max(0, Math.min(100, Math.round(score)));
  const c = pct >= 80 ? "text-emerald-600" : pct >= 60 ? "text-primary" : pct >= 40 ? "text-amber-600" : "text-destructive";
  const r = 52, C = 2 * Math.PI * r;
  return (
    <div className="relative grid h-32 w-32 place-items-center">
      <svg width={128} height={128} viewBox="0 0 128 128" className="rotate-[-90deg]">
        <circle cx={64} cy={64} r={r} className="stroke-muted" strokeWidth={10} fill="none" />
        <circle cx={64} cy={64} r={r} className={c} strokeWidth={10} fill="none"
          strokeDasharray={C} strokeDashoffset={C - (pct / 100) * C} strokeLinecap="round" />
      </svg>
      <div className="absolute text-center">
        <div className={`font-display text-3xl font-bold ${c}`}>{pct}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label.split(" ")[0]}</div>
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, children }: { icon: typeof Brain; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary-soft text-primary"><Icon className="h-4 w-4" /></div>
        <h3 className="font-display text-base font-bold">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function EmptyState({ onGenerate, generating }: { onGenerate: () => void; generating: boolean }) {
  return (
    <div className="rounded-3xl border-2 border-dashed border-border bg-card p-10 text-center shadow-soft">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-primary-soft text-primary">
        <Sparkles className="h-7 w-7" />
      </div>
      <h2 className="mt-4 font-display text-xl font-bold">Meet your AI Revision Coach</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        I'll analyse your grades, quizzes, attendance and Learning Tree progress, then build you a personalised 7-day study plan with predicted exam readiness and daily reminders.
      </p>
      <Button onClick={onGenerate} disabled={generating} className="mt-5 gap-2">
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        Generate my plan
      </Button>
    </div>
  );
}
